import axios from 'axios'
import { Toast } from 'vant'
import router from '../router/index'

const requests = axios.create({
  baseURL: '/api',
  timeout: 50000,
})
// 存储每个请求的标识和取消函数
const pendingRequests = new Map()
// 存储所有请求的取消函数，用于全局取消
const allRequests = new Map()

// 生成请求唯一标识
const generateRequestKey = (config) => {
  const { method, url, data, params } = config
  // 如果有 data 或 params，也加入 key，确保相同参数的请求也被取消
  const dataStr = data ? JSON.stringify(data) : ''
  const paramsStr = params ? JSON.stringify(params) : ''
  return `${method}&${url}&${dataStr}&${paramsStr}`
}

// 取消所有待处理的请求
export const cancelAllRequests = (reason = '页面跳转，取消所有待处理请求') => {
  allRequests.forEach((cancel, key) => {
    cancel(reason)
  })
  allRequests.clear()
  pendingRequests.clear()
}

// 取消指定请求
export const cancelRequest = (url, method = 'get') => {
  const requestKey = `${method}&${url}`
  if (pendingRequests.has(requestKey)) {
    const cancel = pendingRequests.get(requestKey)
    cancel(`取消请求: ${requestKey}`)
    pendingRequests.delete(requestKey)
  }
}

requests.interceptors.request.use((config) => {
  const cancelDuplicate = config.cancelDuplicate ?? true

  // 同一接口防抖：对于同一个URL（忽略参数），取消之前所有未完成的请求
  if (config.enableDebounce !== false) {
    const urlPattern = config.url.split('?')[0]
    const urlKey = `${config.method}&${urlPattern}`
    
    // 查找并取消该URL的所有未完成请求
    const keysToDelete = []
    allRequests.forEach((cancel, key) => {
      if (key.startsWith(urlKey)) {
        cancel(`同一接口防抖取消: ${key}`)
        keysToDelete.push(key)
      }
    })
    
    // 清理已取消的请求记录
    keysToDelete.forEach(key => {
      allRequests.delete(key)
      pendingRequests.delete(key)
    })
  }

  if (cancelDuplicate) {
    const requestKey = generateRequestKey(config)

    if (pendingRequests.has(requestKey)) {
      const cancel = pendingRequests.get(requestKey)
      cancel(`重复请求被取消: ${requestKey}`)
      pendingRequests.delete(requestKey)
    }

    config.cancelToken = new axios.CancelToken(cancel => {
      pendingRequests.set(requestKey, cancel)
      // 同时存储到全局请求列表中
      allRequests.set(requestKey, cancel)
    })
  }

  const authorization = localStorage.getItem('token')
  if (authorization) {
    config.headers['X-Auth-Token'] = authorization
  }

  const contentType = config.contentType || (config.headers && config.headers['Content-Type'])
 debugger
  if (contentType && contentType.includes('application/x-www-form-urlencoded')) {
    if (config.data && typeof config.data === 'object') {
      const urlSearchParams = new URLSearchParams()
      Object.keys(config.data).forEach(key => {
        const value = config.data[key]
        if (value !== null && value !== undefined) {
          urlSearchParams.append(key, String(value))
        }
      })
      config.data = urlSearchParams.toString()
    }
  }

  return config
})

requests.interceptors.response.use(
  (response) => {
    const requestKey = generateRequestKey(response.config)

    // 清理请求记录
    pendingRequests.delete(requestKey)
    allRequests.delete(requestKey)

    const res = response.data

    if (res.code === 200) {
      return res
    } else {
      // 处理业务状态码401（登录过期）
      if (res.code === 401) {
        Toast.fail(res.msg || '登录已过期，请重新登录')
        // 清除所有本地存储的用户相关信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('addScanText')
        // 取消所有待处理的请求
        cancelAllRequests('业务状态码401，取消所有待处理请求')
        // 跳转到登录页，使用replace避免返回按钮回到需要登录的页面
        setTimeout(() => {
          router.replace('/login')
        }, 1000)
      } else {
        Toast.fail(res.msg || '请求失败')
      }
      return Promise.reject(res)
    }
  },
  (error) => {
    const config = error.config
    if (config) {
      const requestKey = generateRequestKey(config)
      pendingRequests.delete(requestKey)
      allRequests.delete(requestKey)
    }

    if (axios.isCancel(error)) {
      // 取消的请求直接返回一个永远pending的Promise，不触发catch
      return new Promise(() => {})
    }

    if (error.response) {
      const status = error.response.status
      if (status === 401) {
        Toast.fail('登录已过期，请重新登录')
        // 清除所有本地存储的用户相关信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('addScanText')
        // 取消所有待处理的请求
        cancelAllRequests('401错误，取消所有待处理请求')
        // 跳转到登录页，使用replace避免返回按钮回到需要登录的页面
        setTimeout(() => {
          router.replace('/login')
        }, 1000)
      } else if (status === 403) {
        Toast.fail('没有权限访问')
      } else if (status === 404) {
        Toast.fail('请求的资源不存在')
      } else if (status === 500) {
        Toast.fail('服务器错误')
      } else {
        Toast.fail(error.response.data?.msg || '请求失败')
      }
    } else if (error.code === 'ECONNABORTED') {
      Toast.fail('请求超时')
    } else {
      Toast.fail('网络错误')
    }

    return Promise.reject(error)
  }
)

// 包装 requests，支持 (config, extraConfig) 这种调用方式
const wrappedRequests = function(config, extraConfig) {
  if (extraConfig && typeof extraConfig === 'object') {
    Object.assign(config, extraConfig)
  }
  return requests(config)
}

// 复制所有属性
Object.assign(wrappedRequests, requests)

export default wrappedRequests
